/* eslint-disable jsx-a11y/click-events-have-key-events */
import { ChannelIcon } from "@audacia-hq/shared/components";
import { faCircleCheck } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import dayjs from "dayjs";
import { Field, FormikProvider, useFormik } from "formik";
import * as React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import * as Yup from "yup";

import { useLocale } from "../../../context/LocaleContext";
import { useMember } from "../../../context/MemberContext";
import { getLastEstimatedItemAndSlot, renderLatenessWarning, useQueue } from "../../../context/QueueContext";
import { Expert } from "../../../models/expert";
import { joinQueue } from "../../../services/api";
import Button from "../../Button";
import ExpertQueueSummary from "../../experts/ExpertQueueSummary";
import PushNotificationsSwitch from "../../notifications/PushNotificationsSwitch";
import useMissingMemberData from "../MissingMemberDataHook";

import CloseButtons from "./CloseButtons";

interface Props {
  expert: Expert;
  isDirectConsultation: boolean;
  onClose: () => void;
  onError: (err: string) => void;
}

const VideoForm: React.FC<Props> = ({
  expert, isDirectConsultation, onClose, onError,
}) => {
  const { t } = useTranslation("consultations");
  const locale = useLocale();
  const { member, changeQueueNotificationChannels } = useMember();
  const { memberQueues } = useQueue();
  const [loading, setLoading] = useState<boolean>(false);
  const [queued, setQueued] = useState<boolean>(!!memberQueues[expert?.expertUid]);
  const [confirmed, setConfirmed] = useState(false);
  const [showAvailabilityInputs, setShowAvailabilityInputs] = useState<boolean>();
  const [isDirectMask, setIsDirectMask] = useState<boolean>();
  const allowPushNotification = !!member?.profile?.queueNotificationChannels?.includes("PUSH");
  const lastEstimatedItemAndSlot = getLastEstimatedItemAndSlot(expert, -2);

  useEffect(() => {
    setQueued(!!memberQueues[expert?.expertUid]);
  }, [memberQueues, expert]);

  const {
    initialValues, validationRules, inputs, onSubmit: onMemberDataSubmit,
  } = useMissingMemberData({ pseudo: true, birthday: true });

  const enqueueVideo = async (memberPseudo: string, dropAt?: string) => {
    const { error } = await
      joinQueue({
        variables: {
          queueUid: expert.queue.queueUid,
          type: "VIDEO",
          expertUid: expert.expertUid,
          language: locale.toUpperCase(),
          memberPseudo,
          dropAt,
        },
      });
    if (error) {
      onError(error.messages[0]);
    } else {
      setConfirmed(true);
      localStorage.removeItem("consultationModal");
    }
    setIsDirectMask(undefined);
    setLoading(false);
  };

  const computeDropAt = (value: string | undefined, visible: boolean) => {
    if (!value || !visible) return undefined;
    const [hour, minute] = value.split(":").map((v) => parseInt(v, 10));
    const now = dayjs().second(0).millisecond(0);
    if (hour < now.hour() || minute < now.minute()) {
      return now.add(1, "day").hour(hour).minute(minute).toISOString();
    }
    return now.hour(hour).minute(minute).toISOString();
  };

  const setMask = React.useCallback(() => {
    setIsDirectMask(isDirectConsultation);
  }, [isDirectConsultation]);

  const formik = useFormik({
    initialValues: {
      dropAt: `${dayjs().add(6, "hour").hour()}:${dayjs().minute()}`,
      ...initialValues,
    },
    validationSchema: Yup.object().shape({
      dropAt: Yup.string(),
      ...validationRules,
    }),
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (form, helpers) => {
      if (!expert) return;

      try {
        setLoading(true);

        await onMemberDataSubmit(form, helpers);
        setMask();
        await enqueueVideo(member?.profile?.pseudo || form?.pseudo, computeDropAt(form?.dropAt, showAvailabilityInputs));
      } catch (err) {
        setLoading(false);
        if (err.message === "pseudo.alreadyUsed") return;
        toast.error(t("common:errors.generalError"));
      }
      setLoading(false);
    },
  });

  const renderQueued = () => (
    <>
      <div className="flex flex-col text-sm p-6 bg-slate-100 rounded-sm gap-3">
        <div className="text-lg font-semibold py-2 text-center">{t("queue.alreadyQueuedTitle")}</div>
        <div className="flex flex-col gap-4 text-center">

          <div>
            {t("queue.alreadyQueuedText", {
              pseudo: expert?.pseudo,
            })}
          </div>
        </div>
      </div>
      <div className="p-6">
        <PushNotificationsSwitch text={t("queue.pushNotification")} checked={allowPushNotification} onChange={changeQueueNotificationChannels} />
      </div>
      <CloseButtons onClose={onClose} />
    </>
  );

  const renderConfirmed = () => (
    <>
      <div className="col-span-6 md:col-span-4 md:col-start-2">
        <div>
          <div className="w-full text-center ">

            <div className="text-lg font-semibold ">{t("video.confirmationTitle")}</div>
            <FontAwesomeIcon
              className="text-3xl z-10 text-green-500 mt-5"
              icon={faCircleCheck}
            />
            <div className="mt-5">
              {t("video.confirmationText", {
                pseudo: expert?.pseudo,
              })}
            </div>
          </div>
        </div>
        <div className="p-6">
          <PushNotificationsSwitch text={t("queue.pushNotification")} checked={allowPushNotification} onChange={changeQueueNotificationChannels} />
        </div>
        {renderLatenessWarning(lastEstimatedItemAndSlot.item?.lateness, t)}
        <CloseButtons onClose={onClose} />
      </div>
    </>
  );

  return (
    <>
      {confirmed && renderConfirmed()}
      {!confirmed && queued && renderQueued()}
      {!confirmed && !queued && (
        <>
          <ExpertQueueSummary channel="VIDEO" expert={expert} />

          <FormikProvider value={formik}>
            {(() => {
              const inputFields = inputs(formik);

              return (
                <div className="flex flex-col items-center gap-4 my-3 w-full">
                  {inputFields.pseudo && (
                    <div className="w-full flex flex-col gap-1 justify-stretch">
                      {inputFields.pseudo()}
                    </div>
                  )}
                  {inputFields.birthday && (
                    <div className="w-full flex flex-col gap-1 ">
                      {inputFields.birthday()}
                    </div>
                  )}
                </div>
              );
            })()}

            <div className="text-center">{t("video.cameraIsOptional")}</div>

            {showAvailabilityInputs && (
              <Field name="dropAt">
                {({ field }) => (
                  <div className="my-6 flex flex-col gap-1">
                    <label htmlFor="phoneSelect">
                      {t("consultationForm.dropAt")}
                    </label>
                    <div className="flex flex-row flex-nowrap gap-4 items-baseline w-full">
                      <input
                        type="time"
                        className="input flex-grow"
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        {...field}
                      />
                      <div
                        className="cursor-pointer text-blue-700 grow-0"
                        onClick={() => setShowAvailabilityInputs(false)}
                      >
                        {t("common:cancel")}
                      </div>
                    </div>
                  </div>
                )}
              </Field>
            )}

            {!(isDirectMask || isDirectConsultation) && !showAvailabilityInputs && (
              <div
                className="text-center cursor-pointer text-blue-700 text-sm mt-6"
                onClick={() => setShowAvailabilityInputs(true)}
              >
                {t("consultationForm.setAvailabilityToggle")}
              </div>
            )}

            <div className="pt-6 text-center">
              <Button
                size="large"
                render="primary"
                onClick={() => formik.submitForm()}
                loading={loading}
                data-pw="consultationSubmit"
              >
                <span className="mr-3"><ChannelIcon channel="VIDEO" className="text-white" /></span>
                <span>{!(isDirectMask || isDirectConsultation) ? t("common:enqueue") : t("common:start")}</span>
              </Button>
            </div>

          </FormikProvider>
        </>
      )}
    </>
  );
};

export default VideoForm;
